import React, { useState, useEffect } from 'react'
import Scrollchor from 'react-scrollchor'
import { FacedRatingButton } from './facedRatingButton'
import renderHTML from 'react-render-html'

import './styles.scss'

const DEFAULT_MESSAGES: { [key: string]: MesageFormat } = {
  DE: {
    happy: [
      'Vielen Dank für Ihr Feedback! Für Sie war die Antwort hilfreich.',
      'Vielen Dank für Ihr Feedback! Für [value] von [sum] Personen war die Antwort hilfreich.'
    ],
    unhappy: [
      'Vielen Dank für Ihr Feedback! Für Sie war die Antwort <u>nicht</u> hilfreich. Teilen Sie uns mit, warum:',
      'Vielen Dank für Ihr Feedback! Für [value] von [sum] Personen war die Antwort <u>nicht</u> hilfreich. Teilen Sie uns mit, warum:'
    ],
    mail: 'interaktiv@tamedia.ch'
  },
  FR: {
    happy: [
      'FR | Vielen Dank für Ihr Feedback! Für Sie war die Antwort hilfreich.',
      'FR | Vielen Dank für Ihr Feedback! Für [value] von [sum] Personen war die Antwort hilfreich.'
    ],
    unhappy: [
      'FR | Vielen Dank für Ihr Feedback! Für Sie war die Antwort <u>nicht</u> hilfreich. Teilen Sie uns mit, warum: interaktiv@tamedia.ch',
      'FR | Vielen Dank für Ihr Feedback! Für [value] von [sum] Personen war die Antwort <u>nicht</u> hilfreich. Teilen Sie uns mit, warum:'
    ],
    mail: 'interaktiv@tamedia.ch'
  }
}
const DEFAULT_MAIL_SUBJECT = 'Online-Beitrag des Interaktiv-Teams'
const DB_ENDPOINT = 'https://ta-user-feedback.firebaseio.com/miniVotings'

interface DatabaseFormat {
  d: number
  u: number
}
export interface MesageFormat {
  happy: [string, string]
  unhappy: [string, string]
  mail: string
}
export interface Props {
  /**
   * Displays a thumb up / thumb down voting
   * with counts of the voting and a message
   */
  elementId: string | number
  /** Subject for the new e-mail opened in user e-mail app */
  subject?: string
  /** ID for the article used as index in database */
  articleID: number | string
  /** Add class 'inverted' to the component */
  inverted?: boolean
  /** Language desired */
  locale?: 'DE' | 'FR' | 'IT' | 'EN'
  /** Messages shown after voting */
  messages?: { [key: string]: MesageFormat }
  /** Target for scrolling - single arrow */
  scrollUpTarget?: string
  /** Target for scrolling - double arrow */
  scrollUpUpTarget?: string
}
const getBarWidth = (vals: DatabaseFormat, what: 'u' | 'd') => {
  return (vals[what] * 100) / (vals.u + vals.d) + '%'
}
export const MiniVoting = ({
  subject = DEFAULT_MAIL_SUBJECT,
  inverted = false,
  articleID,
  locale = 'DE',
  messages = DEFAULT_MESSAGES,
  elementId = 'idMissing',
  scrollUpTarget,
  scrollUpUpTarget
}: Props) => {
  if (!articleID) {
    console.warn('MiniVoting |', 'ArticleID needed', articleID)
    return <></>
  }
  const dbPath = 'minivote-' + articleID + '-' + elementId

  const [isLoading, setIsLoading] = useState<boolean>(false)

  const [obtainedFromDB, setObtainedFromDB] = useState<boolean>(false)
  const [voted, setVote] = useState<'u' | 'd' | undefined>(
    localStorage[dbPath] || undefined
  )
  const [values, setValues] = useState<DatabaseFormat>({ u: 0, d: 0 })
  let messageCount = 0
  let messageType: 'happy' | 'unhappy' = 'happy'
  let prepMessage = ''
  if (voted !== undefined) {
    messageType = voted === 'u' ? 'happy' : 'unhappy'
    messageCount =
      values[voted] < values.u + values.d || values[voted] > 1 ? 1 : 0
    prepMessage = messages[locale][messageType][messageCount]
      .replace('[value]', values[voted].toString())
      .replace('[sum]', (values.u + values.d).toString())
  }

  /**
   * Adds the votes to the cumulative votes of the previous voters.
   *
   */
  const updatePreviousValuesWithNewVotes = (
    previousValue: DatabaseFormat,
    which: 'u' | 'd'
  ) => {
    const currentVotes = JSON.parse(JSON.stringify(previousValue))
    if (!voted) currentVotes[which] += 1
    if (voted) currentVotes[which] -= 1
    return currentVotes
  }
  /**
   * Write the increased value to the database – conditionally. This
   * function will call itself if the precondition isn't met and will just
   * keep on trying.
   */
  const writeToDatabase = (
    currentValues: DatabaseFormat,
    what: 'u' | 'd',
    eTag: string | null
  ): Promise<DatabaseFormat> => {
    return fetch(`${DB_ENDPOINT}/${articleID}/${elementId}.json`, {
      headers: eTag ? { 'if-match': eTag } : undefined,
      method: 'PUT',
      body: JSON.stringify(
        updatePreviousValuesWithNewVotes(currentValues, what)
      )
    }).then(writeResponse =>
      writeResponse.json().then(json => {
        const responseStatus = writeResponse.status

        // Request has failed as data changed before, but luckily we get the new
        // data back right away
        if (responseStatus === 412) {
          const newEtag = writeResponse.headers.get('ETag')
          return writeToDatabase(currentValues, what, newEtag)
        }

        if (responseStatus === 200) {
          // Save the vote status to local storage
          setVote(undefined)
          localStorage.setItem(dbPath, what)
          return json
        }
      })
    )
  }
  const pushVotesToDB = (currentValues: DatabaseFormat, what: 'u' | 'd') => {
    // Get original value
    return fetch(`${DB_ENDPOINT}/${articleID}/${elementId}.json`, {
      headers: { 'X-Firebase-ETag': 'true' }
    })
      .then(originalValueResponse =>
        originalValueResponse.json().then(previousValue => {
          // yes, this is very optimistic programming
          if (!originalValueResponse.ok) {
            throw Error(originalValueResponse.statusText)
          }
          // etag, used to check whether the values have changed between
          // getting the previous values and writing the new values
          const etag = originalValueResponse.headers.get('ETag')
          // No previous values? Apparently you're the first one to vote
          if (!previousValue) {
            return fetch(`${DB_ENDPOINT}/${articleID}/${elementId}.json`, {
              method: 'PUT',
              body: JSON.stringify(
                updatePreviousValuesWithNewVotes(currentValues, what)
              )
            }).then(writeResponse => {
              if (!writeResponse.ok) {
                throw Error(writeResponse.statusText)
              }
              localStorage.setItem(dbPath, what)
              return writeResponse.json()
            })
          }

          return writeToDatabase(currentValues, what, etag)
        })
      )
      .catch((error: Error) => {
        console.log(error.message, error)
        return currentValues
      })
  }

  const clickHandler = (what: 'u' | 'd') => {
    setIsLoading(true)
    pushVotesToDB(values, what)
      .then(downloadedValues => {
        setIsLoading(false)
        setValues(downloadedValues)
        switch (what) {
          case 'u':
            if (voted !== 'u') {
              setVote('u')
            } else if (voted === 'u') {
              setVote(undefined)
            }
            break
          case 'd':
            if (voted !== 'd') {
              setVote('d')
            } else if (voted === 'd') {
              setVote(undefined)
            }
        }
      })
      .catch((error: Error) => {
        console.log(error.message, error)
      })
  }
  useEffect(() => {
    // Get values for this question from DB
    fetch(`${DB_ENDPOINT}/${articleID}/${elementId}.json`).then(response =>
      response.json().then((vals: DatabaseFormat) => {
        if (!vals) {
          vals = { u: 0, d: 0 }
          localStorage.removeItem(dbPath)
        }
        setValues(vals)

        // Request to DB finished, Component can render
        setObtainedFromDB(true)
      })
    )
  }, [])
  if (!obtainedFromDB) return <></>
  const barWidth = getBarWidth(values, localStorage[dbPath])
  return (
    <>
      <div className={'miniVoting ' + (inverted ? 'inverted' : '')}>
        <div className='buttonWrapper'>
          {(voted === undefined || voted === 'u') && (
            <FacedRatingButton
              mood='happy'
              onClick={() => {
                clickHandler('u')
              }}
              isLoading={isLoading}
              state={voted === 'u' ? 'clicked' : ''}
            />
          )}
          {(voted === undefined || voted === 'd') && (
            <FacedRatingButton
              mood='unhappy'
              onClick={() => {
                clickHandler('d')
              }}
              isLoading={isLoading}
              isSecond
              state={voted === 'd' ? 'clicked' : ''}
            />
          )}
          {voted !== undefined && values.d + values.u > values[voted] && (
            <div className={'barWrapper ' + (voted === undefined ? 'off' : '')}>
              <div className='bar' style={{ width: barWidth }} />
            </div>
          )}
          {voted === undefined && (
            <div className='isanswergood'>Ist die Antwort hilfreich?</div>
          )}
          <div className='scrollWrapper'>
            {scrollUpTarget && (
              <Scrollchor to={'#' + scrollUpTarget}>
                <button className='notVoted'>
                  <i className='arrow up icon' />
                </button>
              </Scrollchor>
            )}
            {scrollUpUpTarget && (
              <Scrollchor to={'#' + scrollUpUpTarget}>
                <button className='notVoted'>
                  <i className='angle double up icon' />
                </button>
              </Scrollchor>
            )}
          </div>
        </div>
        {voted !== undefined && (
          <div
            className={
              'message ' + (values.d + values.u <= values[voted] ? 'noBar' : '')
            }
          >
            {renderHTML(prepMessage)}
            {messageType === 'unhappy' ? (
              <a href={`mailto:${messages[locale].mail}?subject=${subject}`}>
                &nbsp; {messages[locale].mail}
              </a>
            ) : (
              ''
            )}
          </div>
        )}
      </div>
    </>
  )
}
